import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementLocale } from '@stripe/stripe-js';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withUnistyles } from 'react-native-unistyles';

import { theme } from '@squire/ui';

import { getPaymentIntentQuery } from '@/api/booking/getPaymentIntentQuery';
import { getStripePromise } from '@/utils/stripeLoader';

// Starts loading at module load; each mount below reuses that promise, or
// retries when the last load was blocked (getStripePromise resets on failure).
getStripePromise();

export const initStripeEagerly = () => {
  // No-op on web: the call above already starts loading at module load.
};

const StyledStripeElements = withUnistyles(Elements);

type StripeProviderProps = PropsWithChildren<{
  excludePaymentMethods?: string[];
}>;

export const StripeProvider = ({
  children,
  excludePaymentMethods,
}: StripeProviderProps) => {
  const { i18n } = useTranslation();
  const [stripePromise] = useState(getStripePromise);
  const { data } = useSuspenseQuery(
    getPaymentIntentQuery(excludePaymentMethods),
  );

  const { client_secret } = data ?? {};

  return (
    <StyledStripeElements
      stripe={stripePromise}
      uniProps={(colors) => ({
        options: {
          clientSecret: client_secret,
          locale: i18n.language as StripeElementLocale,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: colors.backgroundBrand,
              colorBackground: colors.backgroundL1,
              colorText: colors.textPrimary,
              colorTextSecondary: colors.textSecondary,
              colorDanger: colors.textNegative,
              colorSuccess: colors.textPositive,
              colorWarning: colors.textWarning,
              colorTextPlaceholder: colors.textTertiary,
              // fontFamily: 'Inter',
              borderRadius: `${theme.borderRadius.s}px`,
              buttonBorderRadius: `${theme.borderRadius.s}px`,
            },
          },
        },
      })}
    >
      {children}
    </StyledStripeElements>
  );
};
